import { useEffect, useMemo, useRef } from 'react';

import { sliceHtmlByCharCount } from '../lib/html-slice';
import { useAssistantMessageText } from './useAssistantMessageText';
import { useTypewriterReveal } from './useTypewriterReveal';

type UseAssistantMessageDisplayArgs = {
  text: string;
  shouldAnimate?: boolean;
  isStreaming?: boolean;
  onTypingComplete?: () => void;
};

export function useAssistantMessageDisplay({
  text,
  shouldAnimate = false,
  isStreaming = false,
  onTypingComplete,
}: UseAssistantMessageDisplayArgs) {
  const { sanitizedText, plainText, shouldRenderHtml, shouldRender } =
    useAssistantMessageText({ text });

  // Business rule: Typewriter animation reveals plain text at a steady pace.
  const {
    displayedText: animatedPlainText,
    isComplete: isTypingComplete,
  } = useTypewriterReveal(plainText, {
    enabled: shouldAnimate && shouldRender,
  });

  const prevIsCompleteRef = useRef(false);
  const callbackFiredRef = useRef(false);

  // Business rule: Only consider typing complete when not streaming more text.
  const trulyComplete = isTypingComplete && !isStreaming;

  useEffect(() => {
    // Fire callback once when typing completes for this message.
    if (
      shouldAnimate &&
      trulyComplete &&
      !prevIsCompleteRef.current &&
      !callbackFiredRef.current &&
      onTypingComplete
    ) {
      callbackFiredRef.current = true;
      onTypingComplete();
    }
    prevIsCompleteRef.current = trulyComplete;
  }, [trulyComplete, onTypingComplete, shouldAnimate]);

  useEffect(() => {
    // Reset when typing restarts for a new or extended message.
    if (shouldAnimate && !trulyComplete) {
      callbackFiredRef.current = false;
    }
  }, [shouldAnimate, trulyComplete, plainText]);

  // Business rule: Slice sanitized HTML by visible chars to preserve formatting during typing.
  const animatedHtml = useMemo(() => {
    if (!shouldAnimate || isTypingComplete) return sanitizedText;
    const charCount = animatedPlainText.length;
    return sliceHtmlByCharCount(sanitizedText, charCount);
  }, [shouldAnimate, isTypingComplete, sanitizedText, animatedPlainText.length]);

  const displayPlainText = shouldAnimate ? animatedPlainText : plainText;
  const displaySanitizedText = shouldAnimate ? animatedHtml : sanitizedText;
  const displayHtml = shouldRenderHtml;

  return {
    shouldRender,
    displayPlainText,
    displaySanitizedText,
    displayHtml,
    isTypingComplete,
  };
}
